// src/pages/pengamatan/buah-alami/ProcessedConfirmationDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Slide,
  Box,
  Typography,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';

// Transition untuk slide-in effect
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProcessedConfirmationDialog = ({
  open,
  processingData,
  onClose,
  onConfirm,
  currentTab,
  actionType = 'processed', // 'processed' atau 'draft'
  loading = false
}) => {
  // Kelompokkan data berdasarkan id_pengamatan
  const groupedData = processingData.reduce((acc, item) => {
    if (!acc[item.id_pengamatan]) {
      acc[item.id_pengamatan] = {
        id_pengamatan: item.id_pengamatan,
        lokasi: item.lokasi,
        id_lokasi: item.id_lokasi,
        plots: []
      };
    }
    acc[item.id_pengamatan].plots.push(item);
    return acc;
  }, {});

  const groupedDataArray = Object.values(groupedData);

  // Fungsi untuk mendapatkan warna chip berdasarkan status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Processed':
        return 'info';
      case 'Pending':
        return 'warning';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  // Fungsi untuk mendapatkan teks berdasarkan action type
  const getActionTexts = () => {
    if (actionType === 'draft') {
      return {
        title: 'Konfirmasi Perubahan Status ke Draft',
        statusFrom: currentTab === 'Approved' ? 'Approved' : 'Processed',
        statusTo: 'Draft'
      };
    } else {
      return {
        title: 'Konfirmasi Perubahan Status ke Processed',
        statusFrom: 'Approved',
        statusTo: 'Processed'
      };
    }
  };

  const actionTexts = getActionTexts();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: 'min-content',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" component="span">
            {actionTexts.title}
          </Typography>
          {loading && <CircularProgress size={20} />}
        </Box>
      </DialogTitle>

      <DialogContent sx={{paddingBottom: '0px'}}>
        {/* Alert Info */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Apakah Anda yakin akan merubah <strong>{groupedDataArray.length}</strong> Lokasi dan <strong>{processingData.length}</strong> Plot dari status <strong>{actionTexts.statusFrom}</strong> ke <strong>{actionTexts.statusTo}</strong>?
          </Typography>
        </Alert>

        <DialogContentText id="alert-dialog-slide-description">
            
        </DialogContentText>

        {/* Tabel Data */}
        {groupedDataArray.length > 0 ? (
          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              maxHeight: 400,
              overflow: 'auto'
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '15%' }}>
                    ID Pengamatan
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '20%' }}>
                    Lokasi
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '10%' }}>
                    Plot
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '15%' }}>
                    Luas Plot
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '10%' }}>
                    PJP
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1, backgroundColor: 'background.paper', width: '30%' }}>
                    Perubahan Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedDataArray.map((group, groupIndex) => (
                  <React.Fragment key={group.id_pengamatan}>
                    {group.plots.map((plot, plotIndex) => (
                      <TableRow 
                        key={`${group.id_pengamatan}-${plot.plotKey}`}
                        sx={{ 
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                      >
                        {/* ID Pengamatan - rowSpan hanya untuk plot pertama dalam kelompok */}
                        {plotIndex === 0 && (
                          <TableCell 
                            rowSpan={group.plots.length}
                            sx={{ 
                              py: 1,
                              borderRight: '1px solid',
                              borderRightColor: 'divider'
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {group.id_pengamatan || '-'}
                            </Typography>
                          </TableCell>
                        )}
                        
                        {/* Lokasi - rowSpan hanya untuk plot pertama dalam kelompok */}
                        {plotIndex === 0 && (
                          <TableCell 
                            rowSpan={group.plots.length}
                            sx={{ 
                              py: 1,
                              borderRight: '1px solid',
                              borderRightColor: 'divider'
                            }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {group.lokasi || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {group.id_lokasi || '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}
                        
                        {/* Data Plot Individual */}
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {plot.plot || '-'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2">
                            {plot.luas_plot ? `${plot.luas_plot} ha` : '-'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="body2">
                            {plot.pjp || '-'}
                          </Typography>
                        </TableCell>
                        
                        {/* Perubahan Status */}
                        <TableCell sx={{ py: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                            <Chip 
                              label={plot.status_sebelumnya} 
                              color={getStatusColor(plot.status_sebelumnya)}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2" color="text.secondary">
                              →
                            </Typography>
                            <Chip 
                              label={plot.status_baru} 
                              color={getStatusColor(plot.status_baru)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Separator antara kelompok data yang berbeda */}
                    {groupIndex < groupedDataArray.length - 1 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0.5, backgroundColor: 'grey.100' }}>
                          <Box sx={{ height: '1px', backgroundColor: 'divider' }} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              Tidak ada plot dengan status {actionTexts.statusFrom} yang dipilih
            </Typography>
          </Box>
        )}

        
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          color="inherit"
          disabled={loading}
          size="large"
        >
          Batal
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="primary"
          disabled={processingData.length === 0 || loading}
          size="large"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Memproses...' : `Ubah ${processingData.length} Plot Sekarang`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessedConfirmationDialog;